import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useToast } from '../../../../contexts/ToastContext';
import api from '../../../../services/api';
import ConfirmModal from '../../../molecules/ConfirmModal/ConfirmModal';
import './InviteModal.css';

const InviteModal = ({ isOpen, onDismiss, onSubmit, isSubmitting, projectName, triggerRef, projectId }) => {
  const { showToast } = useToast();
  const [emailInput, setEmailInput] = useState('');
  const [existingInvitations, setExistingInvitations] = useState([]);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [resentId, setResentId] = useState(null);
  const [cooldowns, setCooldowns] = useState({});
  const [position, setPosition] = useState({ top: 60, right: 20 });
  const popoverRef = useRef(null);

  useEffect(() => {
    const hasActive = Object.values(cooldowns).some(s => s > 0);
    if (!hasActive) return;
    const timer = setInterval(() => {
      setCooldowns(prev => {
        const next = {};
        let anyLeft = false;
        for (const [id, sec] of Object.entries(prev)) {
          const remaining = sec - 1;
          if (remaining > 0) { next[id] = remaining; anyLeft = true; }
        }
        return anyLeft ? next : {};
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldowns]);

  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    try {
      // Load the actual project MEMBERS (owner + everyone who accepted) AND the
      // still-PENDING invitations in parallel. The panel used to show only
      // pending invites, so people who accepted vanished from view — the owner
      // could never see who had actually joined. Accepted invitations are
      // filtered out of the pending list precisely because they now appear in
      // `members`, so there's no duplicate.
      const [invData, memberData] = await Promise.all([
        api.request(`/projects/${projectId}/invitations`),
        api.request(`/projects/${projectId}/members`).catch(() => ({ members: [] })),
      ]);
      const pending = (invData.invitations || []).filter(inv => inv.status === 'pending');
      setExistingInvitations(pending);
      setMembers(memberData.members || []);
    } catch (err) {
      console.error('Error fetching invitations:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchInvitations();
    }
  }, [isOpen, projectId, fetchInvitations]);

  // Update position on window resize
  useEffect(() => {
    if (!isOpen || !triggerRef?.current) return;

    const updatePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isOpen, triggerRef]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email.toLowerCase());
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleAddEmail = async () => {
    const input = emailInput.trim().toLowerCase();
    
    if (!input) return;

    // Split by commas and clean up
    const emails = input.split(',').map(e => e.trim()).filter(e => e);
    
    if (emails.length === 0) return;

    // Validate all emails
    const invalidEmails = emails.filter(e => !validateEmail(e));
    if (invalidEmails.length > 0) {
      setError(`Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}`);
      return;
    }

    // Check for already invited
    const alreadyInvited = emails.filter(e => 
      existingInvitations.some(inv => inv.email === e)
    );
    if (alreadyInvited.length > 0) {
      setError(`Already invited: ${alreadyInvited.join(', ')}`);
      return;
    }

    // Send invitations. onSubmit returns the API result on success and throws
    // on failure, so we can show an accurate toast via the shared ToastContext.
    try {
      const result = await onSubmit(emails);
      const n = result?.invitations?.length ?? emails.length;
      showToast(`Sent ${n} invitation${n !== 1 ? 's' : ''}`, 'success');
      setEmailInput('');
      setError('');
      await fetchInvitations();
    } catch (err) {
      console.error('Error sending invitations:', err);
      showToast(err?.message || 'Failed to send invitations', 'error');
    }
  };


  const handleDeleteInvitation = async (invitationId) => {
    setDeletingId(invitationId);
    try {
      await api.request(`/invitations/${invitationId}`, {
        method: 'DELETE'
      });
      setExistingInvitations(existingInvitations.filter(inv => inv.invitationId !== invitationId));
      setError('');
    } catch (err) {
      console.error('Error deleting invitation:', err);
      setError('Failed to delete invitation');
    } finally {
      setDeletingId(null);
    }
  };


  const handleRemoveMember = async (memberUserId) => {
    setRemovingMemberId(memberUserId);
    try {
      await api.request(`/projects/${projectId}/members/${memberUserId}`, {
        method: 'DELETE'
      });
      setMembers((prev) => prev.filter((m) => m.userId !== memberUserId));
      setError('');
    } catch (err) {
      console.error('Error removing member:', err);
      showToast(err.message || 'Failed to remove member', 'error');
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleResendInvitation = async (invitationId) => {
    setResendingId(invitationId);
    try {
      await api.request(`/invitations/${invitationId}/resend`, {
        method: 'POST'
      });
      setError('');
      setResentId(invitationId);
      setCooldowns(prev => ({ ...prev, [invitationId]: 30 }));
      setTimeout(() => setResentId(null), 2000);
      await fetchInvitations();
    } catch (err) {
      console.error('Error resending invitation:', err);
      if (err.message.includes('429')) {
        showToast('Please wait before resending', 'warning');
      } else {
        showToast(err.message || 'Failed to resend invitation', 'error');
      }
    } finally {
      setResendingId(null);
    }
  };

  const handleClose = useCallback(() => {
    setEmailInput('');
    setError('');
    setExistingInvitations([]);
    setMembers([]);
    onDismiss();
  }, [onDismiss]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      // While the "Remove member?" confirm dialog is open it is portaled to
      // document.body (OUTSIDE popoverRef), so a mousedown on its own buttons
      // looks "outside" the popover and would close the whole InviteModal on
      // mousedown — BEFORE the button's click handler runs — so the Remove
      // action (the DELETE) never fires and the member is never removed.
      // Let the ConfirmModal own its own dismissal while it is open.
      if (memberToRemove) return;

      const clickedTrigger = triggerRef?.current?.contains(event.target);
      const clickedPopover = popoverRef.current?.contains(event.target);

      if (!clickedPopover && !clickedTrigger) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, triggerRef, handleClose, memberToRemove]);

  if (!isOpen) return null;

  return (
    <div className="share-overlay">
      <div 
        ref={popoverRef}
        className="share-popover"
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          right: `${position.right}px`,
        }}
      >
        {/* Input */}
        <div className="share-input-wrapper">
          <input
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Email or group, separated by commas"
            className="share-input"
            disabled={isSubmitting}
            autoFocus
          />
          <button
            onClick={handleAddEmail}
            disabled={!emailInput.trim() || isSubmitting}
            className="invite-btn"
          >
            Send
          </button>
        </div>
        
        {error && <div className="share-error">{error}</div>}

        {/* List */}
        <div className="share-list">
          {loading ? (
            <div className="share-loading">Loading...</div>
          ) : (
            <>
              {/* Active members: the owner + everyone who accepted an invite. */}
              {members.map((member) => (
                <div key={member.userId} className="share-row">
                  <div className="share-row-left">
                    <div className="share-avatar">
                      {(member.name || member.email || '?')[0].toUpperCase()}
                    </div>
                    <div className="share-info">
                      <div className="share-email">{member.email}</div>
                      <div className="share-status">
                        {member.role === 'owner' ? 'Owner' : 'Member'} • Active
                      </div>
                    </div>
                  </div>

                  {/* Owner can never be removed; every other member can. */}
                  {member.role !== 'owner' && (
                    <div className="share-row-actions">
                      <button
                        onClick={() => setMemberToRemove(member)}
                        disabled={removingMemberId === member.userId}
                        className="share-delete-btn"
                        title="Remove member"
                      >
                        {removingMemberId === member.userId ? '...' : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                          </svg>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {existingInvitations.map((invitation) => (
                <div
                  key={invitation.invitationId}
                  className="share-row"
                >
                  <div className="share-row-left">
                    <div className="share-avatar share-avatar-gray">{invitation.email[0].toUpperCase()}</div>
                    <div className="share-info">
                      <div className="share-email">{invitation.email}</div>
                      <div className="share-status">Sent • Pending</div>
                    </div>
                  </div>
                  
                  <div className="share-row-actions">
                    {resendingId === invitation.invitationId ? (
                      <span className="share-resend-dots">
                        <span>.</span><span>.</span><span>.</span>
                      </span>
                    ) : resentId === invitation.invitationId ? (
                      <span className="share-resend-sent">Sent!</span>
                    ) : cooldowns[invitation.invitationId] > 0 ? (
                      <span className="share-resend-cooldown">{cooldowns[invitation.invitationId]}s</span>
                    ) : (
                      <button
                        onClick={() => handleResendInvitation(invitation.invitationId)}
                        className="share-resend-btn"
                        title="Resend invitation"
                      >
                        Resend
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteInvitation(invitation.invitationId)}
                      disabled={deletingId === invitation.invitationId}
                      className="share-delete-btn"
                      title="Delete invitation"
                    >
                      {deletingId === invitation.invitationId ? '...' : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))}

              {existingInvitations.length === 0 && members.length === 0 && !loading && (
                <div className="share-empty">No members or pending invitations</div>
              )}
            </>
          )}
        </div>

      </div>

      {/* Reuse the shared confirm dialog — no silent delete. */}
      <ConfirmModal
        open={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        title="Remove member?"
        ariaLabel="Remove member confirmation"
        primaryLabel={removingMemberId ? 'Removing…' : 'Remove'}
        primaryLoading={!!removingMemberId}
        onPrimary={() => {
          const target = memberToRemove;
          setMemberToRemove(null);
          if (target) handleRemoveMember(target.userId);
        }}
        secondaryLabel="Cancel"
      >
        <p className="share-confirm-text">
          {memberToRemove?.email
            ? <>Remove <strong>{memberToRemove.email}</strong> from this project? They’ll lose access immediately.</>
            : 'Remove this member from the project?'}
        </p>
      </ConfirmModal>
    </div>
  );
};

export default InviteModal;
